import { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiUpdateUserResponseBody } from '~/lib/api/modules/Users';
import type { IUseUpdateUserMutationValues } from '~/lib/api/modules/Users/hooks/useUpdateUserMutation';
import type { IUserProfileFormValues } from './UserProfileForm';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { useUpdateUserMutation } from '~/lib/api/modules/Users/hooks/useUpdateUserMutation';
import { useUserQuery } from '~/lib/api/modules/Users/hooks/useUserQuery';
import { UserProfileForm } from './UserProfileForm';

type IUserProfileSectionProps = IContainerProps & {
  onSuccess?: (
    outputData: IApiUpdateUserResponseBody,
    inputValues: IUseUpdateUserMutationValues,
  ) => void;
  cancelUrl?: string;
  userId: string;
};

export const UpdateUserProfileForm: React.FC<IUserProfileSectionProps> = (
  props,
) => {
  const { sx, onSuccess, cancelUrl, userId } = props;
  const { t } = useTranslation();
  const userQuery = useUserQuery({});
  const updateUserMutation = useUpdateUserMutation();

  const profile = userQuery.data?.profile;

  const handleSubmit: SubmitHandler<IUserProfileFormValues> = (values) => {
    if (!profile || updateUserMutation.isLoading) {
      return;
    }

    return updateUserMutation.mutateAsync(
      {
        userId,
        body: {
          ...profile,
          firstname: values.firstName,
          lastname: values.lastName,
          username: values.username || profile.username,
          avatar: values.pictures?.length ? values.pictures[0]?.id : '',
          '@email': values.emailAddress,
        },
      },
      { onSuccess },
    );
  };

  return (
    <ContentBlock sx={sx}>
      <ContentBlock.Title>
        {t('UpdateUserProfileForm.about-you')}
      </ContentBlock.Title>

      <ContentBlock.Suspense
        loading={userQuery.isLoading}
        error={userQuery.error}
      >
        {userQuery.data ? (
          <UserProfileForm
            mode="edition"
            defaultValues={{
              firstName: profile?.firstname,
              lastName: profile?.lastname,
              username: profile?.username,
              emailAddress: profile?.email,
              pictures: profile?.avatarId
                ? [
                    {
                      type: 'picture',
                      id: profile.avatarId,
                    },
                  ]
                : undefined,
            }}
            cancelUrl={cancelUrl}
            onSubmit={handleSubmit}
          />
        ) : null}
      </ContentBlock.Suspense>
    </ContentBlock>
  );
};
